import { useEffect } from 'react';

import { createOrgGroupFieldValues, getGroupMemberIds, getUpdatedGroupMembers } from '../../utils/GroupUtils';

import validate from '@usas/ui-web/components/forms/groups/PracticeAttributesFormValidation';

import useOrgGroupPropertiesData from '@usas/ui-web/state/orgGroupProperties/UseOrgGroupPropertiesData';
import useGroupData from '@usas/ui-web/state/group/UseGroupData';
import useGroupCoachesData from '@usas/ui-web/state/groupCoaches/UseGroupCoachesData';
import useGroupFacilitiesData from '@usas/ui-web/state/groupFacilities/UseGroupFacilitiesData';
import useGroupFieldNamesData from '@usas/ui-web/state/groupFieldNames/UseGroupFieldNamesData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import HierarchicalDataUtils from '@usas/ui-web/utils/HierarchicalDataUtils';

const useGroupPracticeAttributes = () => {
  const { GROUP_CONSTANTS } = useGroupFieldNamesData();
  const { groupState, groupMembersState, getGroupMembers, putGroup, putGroupMembers } = useGroupData();
  const { orgGroupPropertiesState, getOrgGroupProperties } = useOrgGroupPropertiesData();
  const { groupCoachesState } = useGroupCoachesData();
  const { groupFacilitiesState } = useGroupFacilitiesData();
  const { formState, errorState, onFormValueChanged, handleSubmit, setFormData, resetForm, onValueTextPairChanged
  } = useForm(getInitialFormState, submitFormCallback, validate);

  useEffect(() => {
    if (groupState.isObjLoaded === true
      && groupMembersState.isArrayLoaded === true && groupMembersState.orgGroupId === groupState.objData.orgGroupId
      && orgGroupPropertiesState.isArrayLoaded === true && orgGroupPropertiesState.groupTypeId === groupState.objData.groupTypeId
      && groupCoachesState.isArrayLoaded === true && groupCoachesState.orgUnitId === groupState.objData.orgUnitId
      && groupFacilitiesState.isArrayLoaded === true && groupFacilitiesState.orgUnitId === groupState.objData.orgUnitId) {
      const groupObj = groupState.objData;
      const groupMembersArray = groupMembersState.arrayData;

      const groupCoachIds = getGroupMemberIds(groupCoachesState.options, groupMembersArray);

      const facilityFieldValue = groupObj.orgGroupFieldValue.find(x => x.groupTypeField?.fieldName === GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_FACILITY)?.fieldValue;
      const targetFacility = groupFacilitiesState.options.find(x => `${x.id}` === facilityFieldValue);

      setFormData({
        ...formState,
        groupCoaches: HierarchicalDataUtils.GetNameIdPairs(groupCoachesState.options, groupCoachIds),
        facilityId: targetFacility?.id || Constants.DEFAULT_ID,
        facilityName: targetFacility?.name || '--',
        startDate: groupObj.orgGroupFieldValue.find(x => x.groupTypeField?.fieldName === GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_START_DATE)?.fieldValue,
        endDate: groupObj.orgGroupFieldValue.find(x => x.groupTypeField?.fieldName === GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_END_DATE)?.fieldValue
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgGroupPropertiesState, groupState, groupCoachesState, groupFacilitiesState, groupMembersState]);

  useEffect(() => {
    if (groupState.isObjLoaded === true && groupState.objData.groupTypeId !== orgGroupPropertiesState.groupTypeId) {
      getOrgGroupProperties(groupState.objData.groupTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupState, orgGroupPropertiesState]);

  async function submitFormCallback(formState) {
    if (groupState.isObjLoading === false) {
      const newOrgGroupObj = JSON.parse(JSON.stringify(groupState.objData));
      const newGroupMembersArray = getUpdatedGroupMembers(formState.groupCoaches, groupCoachesState.options, groupMembersState.arrayData);

      newOrgGroupObj.orgGroupFieldValue = createOrgGroupFieldValues([
        { fieldName: GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_START_DATE, fieldValue: formState.startDate },
        { fieldName: GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_END_DATE, fieldValue: formState.endDate },
        { fieldName: GROUP_CONSTANTS.ORG_GROUP_TYPE_FIELD_NAME_FACILITY, fieldValue: formState.facilityId }
      ], orgGroupPropertiesState.arrayData, newOrgGroupObj.orgGroupId);

      const putGroupResult = await putGroup(newOrgGroupObj.orgGroupId, newOrgGroupObj);

      if (putGroupResult) {
        if (newGroupMembersArray.length > 0) {
          const putGroupMembersResult = await putGroupMembers(groupState.objData.orgGroupId, newGroupMembersArray, 'Coach');

          if (putGroupMembersResult) {
            getGroupMembers(groupState.objData.orgGroupId);
          }
        }
      }
    }
  }

  function getInitialFormState() {
    return {
      groupCoaches: [],
      facilityId: Constants.DEFAULT_ID,
      facilityName: '--',
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING
    };
  }

  return {
    isSaving: groupState.isObjLoading || groupMembersState.isObjLoading,
    isLoading: orgGroupPropertiesState.isArrayLoading || groupMembersState.isArrayLoading,
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    onCancelClicked: resetForm,
    orgUnitId: groupState.objData.orgUnitId || ''
  };
};

export default useGroupPracticeAttributes;